import React, { useCallback, useState, useMemo } from 'react';
import { Typography, Grid, CircularProgress, Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { Project, ProjectFolder, UserId, User, UserRole } from '@clef/shared/types';
import { ApiResponseLoader, VirtualGrid, useStateSyncSearchParams } from '@clef/client-library';

import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useSetSelectedProjectId } from '@/serverStore/projects';
import {
  SortOption,
  sortProjects,
  filterProjects,
  QueryTag,
  getDescendantFolderIds,
} from '../../utils/project_utils';
import useStyles from './styles';
import CLEF_PATH from '../../constants/path';
import { ProjectCard } from '../../components/ProjectCard/NewProjectCard';
import { FolderCard } from '../../components/ProjectCard/FolderCard';
import { ProjectsToolbar } from './components/ProjectsToolbar';
import { WorkflowTutorialBox } from './components/WorkflowTutorialBox';
import { FolderBreadcrumb } from './components/FolderBreadcrumb';
import { CreateProjectCard } from '../../components/ProjectCard/CreateProjectCard';
import DeleteFolderDialog from '../../components/Dialogs/DeleteFolderDialog';
import MoveToFolderDialog from '../../components/Dialogs/MoveToFolderDialog';
import { isProjectOwner } from '@clef/shared/utils';
import { useGetProjectsQuery } from '@/serverStore/projects';
import { useGetAllProjectFoldersQuery } from '@/serverStore/projectFolders';
import ProjectListView from './components/ProjectListView';
import {
  filterOptionAtom,
  ProjectsViewMode,
  viewModeAtom,
  SearchScope,
  ItemType,
} from '@/uiStates/projects/projectsStates';
import { useAtom } from 'jotai';

type GridItem = {
  id: string | number;
  itemType: 'create' | 'folder' | 'project';
  data?: Project | ProjectFolder;
};

const PROJECT_CARD_MARGIN = 28;

const Projects: React.FC = () => {
  const styles = useStyles();
  const history = useHistory();

  // Get current folder ID from URL query params
  const [folderIdParam, setFolderIdParam] = useStateSyncSearchParams('folderId', '');
  const currentFolderId = folderIdParam ? Number(folderIdParam) : null;

  const currentUser = useTypedSelector(state => state.login.user!);
  const {
    data: projectListData,
    isLoading: projectListLoading,
    error: projectListError,
  } = useGetProjectsQuery();
  const { data: foldersData, isLoading: foldersLoading } = useGetAllProjectFoldersQuery();
  const setSelectedProjectId = useSetSelectedProjectId();
  const [searchText, setSearchText] = useState<string>('');
  const [queryTags, setQueryTags] = useState<QueryTag[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.LastOpened);
  const [filterOption, setFilterOption] = useAtom(filterOptionAtom);
  const [viewMode, setViewMode] = useAtom(viewModeAtom);
  const [searchScope, setSearchScope] = useState<SearchScope>(SearchScope.CurrentFolder);
  const [itemType, setItemType] = useState<ItemType>(ItemType.IncludeFolders);
  const [folderToDelete, setFolderToDelete] = useState<ProjectFolder | null>(null);
  const [isDeleteFolderDialogOpen, setIsDeleteFolderDialogOpen] = useState(false);
  const [itemToMove, setItemToMove] = useState<
    { type: 'folder'; folder: ProjectFolder } | { type: 'project'; project: Project } | null
  >(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

  const users = useTypedSelector(state => state.user.users);

  const userById = useMemo(() => {
    return users.reduce((accum: { [key: UserId]: User }, val) => {
      accum[val.id] = val;
      return accum;
    }, {});
  }, [users]);

  // Build folder path map for displaying in search results
  const folderPathMap = useMemo(() => {
    if (!foldersData) return new Map<number | null, string>();
    const pathMap = new Map<number | null, string>();
    pathMap.set(null, t('Projects'));

    const getPath = (folderId: number | null): string => {
      if (folderId === null) return t('Projects');
      if (pathMap.has(folderId)) return pathMap.get(folderId)!;

      const folder = foldersData.find(f => f.id === folderId);
      if (!folder) return t('Projects');

      const parentPath = getPath(folder.parentFolderId);
      const fullPath = `${parentPath} / ${folder.name}`;
      pathMap.set(folderId, fullPath);
      return fullPath;
    };

    foldersData.forEach(folder => getPath(folder.id));
    return pathMap;
  }, [foldersData]);

  // Get all folder IDs in current folder's subtree (for scoped search)
  const currentFolderSubtreeIds = useMemo(() => {
    if (currentFolderId === null) return null; // At root, include all
    if (!foldersData) return new Set<number | null>([currentFolderId]);

    const descendants = getDescendantFolderIds(currentFolderId, foldersData);
    const subtreeIds = new Set<number | null>([currentFolderId, ...descendants]);
    return subtreeIds;
  }, [foldersData, currentFolderId]);

  // Filter folders to show only those in current folder
  const currentFolders = useMemo(() => {
    if (!foldersData) return [];
    return foldersData.filter(folder => folder.parentFolderId === currentFolderId);
  }, [foldersData, currentFolderId]);

  // Filter projects based on current folder and search scope
  const currentProjects = useMemo(() => {
    if (!projectListData) return [];
    return projectListData.filter(project => {
      // At root level, show projects with null or undefined folderId
      if (currentFolderId === null) {
        return project.projectFolderId == null;
      }
      return project.projectFolderId === currentFolderId;
    });
  }, [projectListData, currentFolderId]);

  // Projects to search (depends on search scope)
  const searchableProjects = useMemo(() => {
    if (!projectListData) return [];
    const hasSearch = searchText || queryTags.length > 0;

    // If not searching, use current folder projects
    if (!hasSearch) {
      return currentProjects;
    }

    // At root level, always search all projects (no scope toggle shown)
    if (currentFolderId === null) {
      return projectListData;
    }

    // If searching with AllFolders scope, search all projects
    if (searchScope === SearchScope.AllFolders) {
      return projectListData;
    }

    // If searching with CurrentFolder scope, search current folder + subfolders
    if (currentFolderSubtreeIds) {
      return projectListData.filter(project => {
        const folderId = project.projectFolderId ?? null;
        return currentFolderSubtreeIds.has(folderId);
      });
    }

    return currentProjects;
  }, [
    projectListData,
    currentProjects,
    searchText,
    queryTags,
    searchScope,
    currentFolderId,
    currentFolderSubtreeIds,
  ]);

  const onProjectItemClick = useCallback(
    async (projectId: number) => {
      await setSelectedProjectId(projectId);
      history.push(`${CLEF_PATH.root}/${currentUser.orgId}/pr/${projectId}`);
    },
    [currentUser.orgId, history, setSelectedProjectId],
  );

  const onFolderClick = useCallback(
    (folderId: number) => {
      setFolderIdParam(String(folderId));
    },
    [setFolderIdParam],
  );

  const onBreadcrumbNavigate = useCallback(
    (folderId: number | null) => {
      setFolderIdParam(folderId === null ? '' : String(folderId));
    },
    [setFolderIdParam],
  );

  const onFolderDelete = useCallback((folder: ProjectFolder) => {
    setFolderToDelete(folder);
    setIsDeleteFolderDialogOpen(true);
  }, []);

  const handleDeleteFolderDialogClose = useCallback(() => {
    setIsDeleteFolderDialogOpen(false);
    setFolderToDelete(null);
  }, []);

  const onFolderMove = useCallback((folder: ProjectFolder) => {
    setItemToMove({ type: 'folder', folder });
    setIsMoveDialogOpen(true);
  }, []);

  const onProjectMove = useCallback((project: Project) => {
    setItemToMove({ type: 'project', project });
    setIsMoveDialogOpen(true);
  }, []);

  const handleMoveDialogClose = useCallback(() => {
    setIsMoveDialogOpen(false);
    setItemToMove(null);
  }, []);

  if (projectListLoading || foldersLoading) {
    return (
      <div className={styles.container}>
        <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
          <CircularProgress size="26px" data-testid="api-response-loader" />
        </Box>
      </div>
    );
  }

  // Only show empty state when at root level and there are no projects or folders
  if (
    !projectListError &&
    !projectListLoading &&
    projectListData &&
    projectListData.length === 0 &&
    (!foldersData || foldersData.length === 0) &&
    currentFolderId === null
  ) {
    return (
      <div className={styles.container}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="70vh"
        >
          <Typography variant="h2" className={styles.emptyStateTitle}>
            {t('You don’t have any projects yet')}
          </Typography>
          <Typography className={styles.emptyStateDescription}>
            {t(
              'Train your first computer vision model by following the 4 steps illustrated below. Hover over each step to see more details. We will also guide you along the way as you get started.',
            )}
          </Typography>

          <WorkflowTutorialBox />
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ProjectsToolbar
        searchText={searchText}
        queryTags={queryTags}
        setQueryTags={setQueryTags}
        setSearchText={setSearchText}
        sortOption={sortOption}
        setSortOption={setSortOption}
        filterOption={filterOption}
        setFilterOption={setFilterOption}
        viewMode={viewMode}
        setViewMode={setViewMode}
        currentFolderId={currentFolderId}
        searchScope={searchScope}
        setSearchScope={setSearchScope}
        itemType={itemType}
        setItemType={setItemType}
        isCollaborator={currentUser.userRole === UserRole.Collaborator}
      />
      <FolderBreadcrumb
        currentFolderId={currentFolderId}
        folders={foldersData ?? []}
        onNavigate={onBreadcrumbNavigate}
      />

      <WorkflowTutorialBox
        className={styles.workflowTutorialBox}
        enableClose
        showStartButton={false}
      />

      <ApiResponseLoader
        response={projectListData}
        loading={!!projectListLoading}
        error={projectListError ?? undefined}
        defaultHeight="30vh"
      >
        {() => {
          // Filter and sort projects based on search scope
          const searchBarHasContent = !!searchText || queryTags.length !== 0;
          const projectsToFilter = searchBarHasContent ? searchableProjects : currentProjects;
          let newProjects = filterProjects(
            projectsToFilter,
            searchText,
            filterOption,
            currentUser.id,
            userById,
            queryTags,
          );
          newProjects = sortProjects(newProjects, sortOption, currentUser.id);

          // Determine which folders to show based on itemType filter
          let foldersToShow: ProjectFolder[] = [];
          if (itemType === ItemType.IncludeFolders) {
            if (searchBarHasContent) {
              // When searching with "Include Folders", filter folders by search text
              const searchLower = searchText.toLowerCase();
              foldersToShow = (foldersData ?? []).filter(folder =>
                folder.name.toLowerCase().includes(searchLower),
              );
            } else {
              // When not searching, show current folder's subfolders
              foldersToShow = currentFolders;
            }
          }
          // When itemType is ProjectsOnly, foldersToShow stays empty

          if (viewMode === ProjectsViewMode.List) {
            return (
              <ProjectListView
                projects={newProjects}
                folders={foldersToShow}
                onProjectItemClick={onProjectItemClick}
                onFolderClick={onFolderClick}
                onFolderDelete={onFolderDelete}
                onFolderMove={onFolderMove}
                onProjectMove={onProjectMove}
                folderPathMap={folderPathMap}
                isSearching={searchBarHasContent}
              />
            );
          }

          // Build grid items: create card first, then folders, then projects
          const gridItems: GridItem[] = [];
          if (!searchBarHasContent) {
            gridItems.push({ id: 'create-project-card', itemType: 'create' });
          }
          foldersToShow.forEach(folder => {
            gridItems.push({ id: `folder-${folder.id}`, itemType: 'folder', data: folder });
          });
          newProjects.forEach(project => {
            gridItems.push({ id: `project-${project.id}`, itemType: 'project', data: project });
          });

          return gridItems.length > 0 ? (
            <VirtualGrid
              itemPerRowCap={4}
              imageRatio={2 / 3}
              componentList={gridItems}
              style={{ marginRight: -PROJECT_CARD_MARGIN }}
            >
              {(item: GridItem) => {
                if (item.itemType === 'create') {
                  return (
                    <CreateProjectCard
                      style={{
                        width: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                        height: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                        aspectRatio: '1.55',
                      }}
                      currentFolderId={currentFolderId}
                    />
                  );
                }
                if (item.itemType === 'folder') {
                  const folder = item.data as ProjectFolder;
                  const creatorUser = folder.createdById ? userById[folder.createdById] : undefined;
                  const isCollaborator = currentUser.userRole === UserRole.Collaborator;
                  return (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      style={{
                        width: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                        height: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                        aspectRatio: '3 / 2',
                      }}
                      onClick={onFolderClick}
                      onDelete={onFolderDelete}
                      onMove={onFolderMove}
                      creatorUser={creatorUser}
                      isCurrentUserCreator={currentUser.id === folder.createdById}
                      hideMoreActionButton={isCollaborator}
                    />
                  );
                }
                const project = item.data as Project;
                const projectOwner = project.usersRoles?.find(user => isProjectOwner(user.role));
                return (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    ownerUser={userById[projectOwner?.userId ?? '']}
                    isCurrentUserOwner={currentUser.id === projectOwner?.userId}
                    style={{
                      width: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                      height: `calc(100% - ${PROJECT_CARD_MARGIN}px)`,
                      aspectRatio: '3 / 2',
                    }}
                    onClick={onProjectItemClick}
                    onMove={onProjectMove}
                  />
                );
              }}
            </VirtualGrid>
          ) : (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              className={styles.projectsEmptyStateContainer}
            >
              <Typography variant="body1" className={styles.projectsEmptyStateText}>
                {t('No projects to display')}
              </Typography>
            </Grid>
          );
        }}
      </ApiResponseLoader>

      <DeleteFolderDialog
        folder={folderToDelete}
        open={isDeleteFolderDialogOpen}
        onClose={handleDeleteFolderDialogClose}
      />

      <MoveToFolderDialog
        item={itemToMove}
        open={isMoveDialogOpen}
        onClose={handleMoveDialogClose}
        isCollaborator={currentUser.userRole === UserRole.Collaborator}
      />
    </div>
  );
};

export default Projects;
